// 问题诊断系统类型定义

export type SystemType = 'machine' | 'organism' | 'social' | 'ecosystem';
export type PrincipleType = 'materiality' | 'structure' | 'relationship' | 'dynamics' | 'systemic';
export type DiagnosisStep = 'input' | 'triage' | 'analysis' | 'crossConsult' | 'report';
export type SessionStatus = 'active' | 'completed' | 'abandoned';

// 系统定义
export interface SystemDefinition {
  id: SystemType;
  name: string;
  icon: React.ReactNode;
  keywords: string;
  description: string;
  color: string;
  borderColor: string;
}

// 五大原理问题
export interface PrincipleQuestion {
  id: PrincipleType;
  principle: string;
  question: string;
  placeholder: string;
  order: number;
}

// 用户回答
export interface UserAnswer {
  principle_type: PrincipleType;
  question_text: string;
  user_answer: string;
  answer_order: number;
}

// 问题诊断会话
export interface DiagnosisSession {
  id: string;
  user_id: string;
  problem_description: string;
  selected_system?: SystemType;
  current_step: DiagnosisStep;
  status: SessionStatus;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  
  // 新增字段：支持草稿和编辑功能
  draft_problem_description?: string; // 草稿问题描述
  draft_answers?: UserAnswer[]; // 草稿回答
  last_edited_at?: string; // 最后编辑时间
  edit_count?: number; // 编辑次数
}

// 原理回答记录
export interface PrincipleAnswer {
  id: string;
  session_id: string;
  principle_type: PrincipleType;
  question_text: string;
  user_answer: string;
  answer_order: number;
  created_at: string;
}

// 系统发现
export interface SystemFinding {
  system: string;
  finding: string;
}

// 下一步建议
export interface NextStep {
  level: string;
  description: string;
}

// 报告元数据（用于报告列表显示）
export interface ReportMetadata {
  id: string;
  title: string;
  created_at: string;
  confidence_score?: number;
  version: number;
  is_latest: boolean;
  generation_trigger: ReportGenerationTrigger;
  summary?: string; // 报告摘要
}

// 编辑状态
export interface EditState {
  isEditing: boolean;
  editingField?: 'problem_description' | 'answers' | null;
  hasUnsavedChanges: boolean;
  lastSavedAt?: string;
  autoSaveEnabled: boolean;
}

// 草稿数据
export interface DraftData {
  problem_description?: string;
  answers?: Partial<UserAnswer>[];
  last_modified: string;
  field_name: string;
}

// 标签页类型
export type DiagnosisDetailTab = 'input' | 'reports';

// 页面状态
export interface DiagnosisDetailState {
  session: DiagnosisSession | null;
  activeTab: DiagnosisDetailTab;
  loading: boolean;
  saving: boolean;
  selectedReportId: string | null;
  editState: EditState;
  reports: DiagnosisReport[];
  error: string | null;
}

// 报告生成触发原因
export type ReportGenerationTrigger = 'initial' | 'regenerated' | 'edited';

// 诊断报告
export interface DiagnosisReport {
  id: string;
  session_id: string;
  title: string;
  core_conclusion: string;
  system_findings: SystemFinding[];
  next_steps: NextStep[];
  ai_analysis?: any;
  confidence_score?: number;
  created_at: string;
  updated_at: string;
  
  // 新增字段：支持版本控制和报告管理
  version: number; // 报告版本号
  is_latest: boolean; // 是否为最新版本
  generation_trigger: ReportGenerationTrigger; // 生成触发原因
  parent_report_id?: string; // 父报告ID（用于版本链）
  is_deleted?: boolean; // 软删除标记
}

// 问题模板
export interface QuestionTemplate {
  id: string;
  system_type: SystemType;
  principle_type: PrincipleType;
  question_text: string;
  placeholder_text?: string;
  question_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// 移除会话分享接口

// API 请求/响应类型

// 保存会话请求
export interface SaveSessionRequest {
  problem_description: string;
  selected_system?: SystemType;
  current_step: DiagnosisStep;
  answers?: UserAnswer[];
  session_id?: string;
}

// 保存会话响应
export interface SaveSessionResponse {
  success: boolean;
  session: DiagnosisSession;
  message: string;
}

// 生成报告请求
export interface GenerateReportRequest {
  session_id: string;
  problem_description: string;
  selected_system: SystemType;
  answers: UserAnswer[];
}

// 生成报告响应
export interface GenerateReportResponse {
  success: boolean;
  report: DiagnosisReport;
  report_id: string;
}

// 更新会话内容请求
export interface UpdateSessionContentRequest {
  problem_description?: string;
  answers?: UserAnswer[];
  save_as_draft?: boolean;
}

// 更新会话内容响应
export interface UpdateSessionContentResponse {
  success: boolean;
  session: DiagnosisSession;
  message?: string;
}

// 保存草稿请求
export interface SaveDraftRequest {
  problem_description?: string;
  answers?: Partial<UserAnswer>[];
  field_name: string;
}

// 保存草稿响应
export interface SaveDraftResponse {
  success: boolean;
  saved_at: string;
  message?: string;
}

// 获取会话报告请求
export interface GetSessionReportsRequest {
  session_id: string;
  include_deleted?: boolean;
}

// 获取会话报告响应
export interface GetSessionReportsResponse {
  success: boolean;
  reports: ReportMetadata[];
  total_count: number;
}

// 获取单个报告响应
export interface GetReportResponse {
  success: boolean;
  report: DiagnosisReport;
}

// 删除报告请求
export interface DeleteReportRequest {
  report_id: string;
  soft_delete?: boolean;
}

// 删除报告响应
export interface DeleteReportResponse {
  success: boolean;
  message: string;
}

// 获取问题响应
export interface GetQuestionsResponse {
  success: boolean;
  system_type: SystemType;
  questions: PrincipleQuestion[];
  total_count: number;
}

// 诊断历史项
export interface DiagnosisHistoryItem {
  id: string;
  problem_description: string;
  selected_system?: SystemType;
  current_step: DiagnosisStep;
  status: SessionStatus;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  has_report: boolean;
  // 支持多个诊断报告
  diagnosis_reports?: {
    id: string;
    title: string;
    core_conclusion: string;
    system_findings?: any[];
    next_steps?: any[];
    ai_analysis?: any;
    confidence_score?: number;
    created_at: string;
  }[];
  // 保持向后兼容
  report?: {
    id: string;
    title: string;
    core_conclusion: string;
    confidence_score?: number;
    created_at: string;
  };
  answers?: PrincipleAnswer[];
}

// 分页信息
export interface Pagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

// 获取历史响应
export interface GetHistoryResponse {
  success: boolean;
  sessions: DiagnosisHistoryItem[];
  pagination?: Pagination;
}

// 前端状态管理类型
export interface DiagnosisState {
  currentStep: DiagnosisStep;
  problemDescription: string;
  selectedSystem: SystemDefinition | null;
  currentQuestionIndex: number;
  userAnswers: UserAnswer[];
  currentAnswer: string;
  diagnosisReport: DiagnosisReport | null;
  sessionId: string | null;
  loading: boolean;
  error: string | null;
}

// 错误类型
export interface DiagnosisError {
  code: string;
  message: string;
  details?: any;
}

// 服务类接口
export interface DiagnosisService {
  // 会话管理
  createSession(problemDescription: string): Promise<DiagnosisSession>;
  updateSession(sessionId: string, data: Partial<DiagnosisSession>): Promise<DiagnosisSession>;
  getSession(sessionId: string): Promise<DiagnosisHistoryItem>;
  
  // 问题和答案
  getQuestions(systemType: SystemType): Promise<PrincipleQuestion[]>;
  saveAnswers(sessionId: string, answers: UserAnswer[]): Promise<void>;
  
  // 报告生成
  generateReport(request: GenerateReportRequest): Promise<DiagnosisReport>;
  
  // 历史记录
  getHistory(page?: number, limit?: number, status?: SessionStatus): Promise<GetHistoryResponse>;
  
  // 分享功能
  // 移除会话分享方法
  
  // 新增：内容编辑功能
  updateSessionContent(sessionId: string, data: UpdateSessionContentRequest): Promise<UpdateSessionContentResponse>;
  saveDraft(sessionId: string, data: SaveDraftRequest): Promise<SaveDraftResponse>;
  
  // 新增：报告管理功能
  getSessionReports(sessionId: string): Promise<GetSessionReportsResponse>;
  getReport(reportId: string): Promise<GetReportResponse>;
  deleteReport(reportId: string, softDelete?: boolean): Promise<DeleteReportResponse>;
}

// 常量定义
export const SYSTEM_TYPES: Record<SystemType, string> = {
  machine: '机械系统',
  organism: '生理系统',
  social: '社会系统',
  ecosystem: '生态系统'
};

export const PRINCIPLE_TYPES: Record<PrincipleType, string> = {
  materiality: '物质性',
  structure: '结构性',
  relationship: '关系性',
  dynamics: '动态性',
  systemic: '系统性'
};

export const DIAGNOSIS_STEPS: Record<DiagnosisStep, string> = {
  input: '问题输入',
  triage: '分诊挂号',
  analysis: '深度剖析',
  crossConsult: '跨科会诊',
  report: '诊断报告'
};

export const SESSION_STATUS: Record<SessionStatus, string> = {
  active: '进行中',
  completed: '已完成',
  abandoned: '已放弃'
};

// 新增常量
export const REPORT_GENERATION_TRIGGERS: Record<ReportGenerationTrigger, string> = {
  initial: '初次生成',
  regenerated: '重新生成',
  edited: '编辑后生成'
};

export const DIAGNOSIS_DETAIL_TABS: Record<DiagnosisDetailTab, string> = {
  input: '用户录入',
  reports: 'AI诊断报告'
};

// 配置常量
export const DIAGNOSIS_CONFIG = {
  AUTO_SAVE_DELAY: 3000, // 自动保存延迟（毫秒）
  MAX_DRAFT_AGE: 7 * 24 * 60 * 60 * 1000, // 草稿最大保存时间（7天）
  MAX_REPORTS_PER_SESSION: 10, // 每个会话最大报告数量
  MARKDOWN_MAX_LENGTH: 50000, // Markdown内容最大长度
} as const;

// 错误代码
export const DIAGNOSIS_ERROR_CODES = {
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  INVALID_INPUT: 'INVALID_INPUT',
  SAVE_FAILED: 'SAVE_FAILED',
  GENERATION_FAILED: 'GENERATION_FAILED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  NETWORK_ERROR: 'NETWORK_ERROR',
} as const;
